/** @jsxImportSource @emotion/react */
import React from "react";
import moment from "moment";
import Immutable from "immutable";
import {CustomThemeContext} from "js/common/themes/CustomThemeProvider";
import * as Formatter from "js/common/utils/formatter";
import * as KpiRepo from "js/common/repo/backbone/kpi-repo";
import * as ratioFormatter from "js/common/utils/ratio-formatter";
import * as Overlays from "js/dashboards/components/overlays";
import KpiPicker from "js/common/views/kpis/kpi-picker";
import RatioPicker from "js/common/views/inputs/ratio-picker";
import * as RatioRepo from "js/common/repo/ratio-repo";
import DataConfigToolbar from "js/dashboards/data-config-toolbar";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import * as KpiCalculator from "js/common/kpi-calculator";
import * as TimeframeRepo from "js/common/repo/backbone/timeframe-repo";
import * as DashboardUtils from "js/dashboards/utils";
import {createCommonConfig} from "js/dashboards/ai-assistant/utils";
import {computePriorTimeframe, isPriorPeriodComputable} from "js/dashboards/components/prior-period";
import {formatPriorPeriodVsLabel, formatChangePct} from "js/dashboards/components/comparison-utils";

// --- Pure helpers (exported for testing) ---

// Baseline always sits at midpoint (0.5). The scale radiates symmetrically outward
// from there by ±nextNiceBound(diff), so the far edge on the dot's side represents a
// real value on the axis (baseline ± range) — not the bare delta.
// compareTo < baseline: dot left of midpoint; left edge labels baseline - range.
// compareTo > baseline: dot right of midpoint; right edge labels baseline + range.
// Returns {dotPos, tickPos: 0.5, edgeLabel: {side, value}} or null when a value is null.
export const calcBarPositions = (compareToValue, baselineValue) => {
  if (compareToValue === null || baselineValue === null) return null;

  if (compareToValue <= baselineValue) {
    // Dot left of midpoint; scale by diff so extreme baselines don't squish the dot
    const diff = baselineValue - compareToValue;
    if (diff === 0) return {dotPos: 0.5, tickPos: 0.5, edgeLabel: {side: "left", value: baselineValue}};
    const nextPow = nextNiceBound(diff);
    const raw = 0.5 - (diff / nextPow) * 0.5;
    return {
      dotPos: Math.min(0.5, Math.max(0, raw)),
      tickPos: 0.5,
      edgeLabel: {side: "left", value: baselineValue - nextPow}
    };
  } else {
    // Dot right of midpoint; right half spans baseline → baseline + nextPow(diff)
    const diff = compareToValue - baselineValue;
    const nextPow = nextNiceBound(diff);
    const raw = 0.5 + (diff / nextPow) * 0.5;
    return {
      dotPos: Math.max(0.5, Math.min(1, raw)),
      tickPos: 0.5,
      edgeLabel: {side: "right", value: baselineValue + nextPow}
    };
  }
};

export const calcChangePct = (compareToValue, baselineValue) => {
  if (compareToValue === null || baselineValue === null) return null;
  if (compareToValue === baselineValue) return 0;
  if (compareToValue === 0 || baselineValue <= 0) return null;
  return ((compareToValue - baselineValue) / baselineValue) * 100;
};

// "good" = compareTo is better; "bad" = compareTo is worse; "neutral" = same or N/A
// Uses raw values so negative baselines don't flip the sign via percentage arithmetic.
export const getBadgeVariant = (compareToValue, baselineValue, higherIsBetter) => {
  if (compareToValue === null || baselineValue === null || compareToValue === baselineValue) return "neutral";
  const compareToIsHigher = compareToValue > baselineValue;
  return (compareToIsHigher === higherIsBetter) ? "good" : "bad";
};

// Returns null when second is 0 (cannot compute ratio)
export const getRatioNumericValue = (firstValue, secondValue) => {
  if (secondValue === 0) return null;
  return firstValue / secondValue;
};

// Smallest 1/2/5 × 10^n that is strictly greater than diff — tighter scale than pure power of 10.
const nextNiceBound = diff => {
  const pow = Math.pow(10, Math.floor(Math.log10(diff)));
  for (const mult of [1, 2, 5, 10]) {
    const candidate = pow * mult;
    if (candidate > diff) return candidate;
  }
  return pow * 10;
};

// Returns the hex colour for the hero section primary value based on variant
export const getHeroValueColor = variant => {
  if (variant === "good") return "#107C10";
  if (variant === "bad") return "#DA4453";
  return "#424242";
};

// Round to at most 2 decimals to avoid floating-point artefacts (e.g. 1.67 + 0.5)
const round2 = n => Math.round(n * 100) / 100;

// Thresholds compare magnitude, not the signed value, so negative axis bounds
// (baseline - range can easily go below zero) abbreviate rather than print in full.
export const abbreviateNumber = n => {
  const magnitude = Math.abs(n);
  const sign = n < 0 ? "-" : "";
  if (magnitude >= 1_000_000_000_000) return `${sign}${round2(magnitude / 1_000_000_000_000)}T`;
  if (magnitude >= 1_000_000_000) return `${sign}${round2(magnitude / 1_000_000_000)}B`;
  if (magnitude >= 1_000_000) return `${sign}${round2(magnitude / 1_000_000)}M`;
  if (magnitude >= 1_000) return `${sign}${round2(magnitude / 1_000)}K`;
  return String(round2(n));
};

// Below this gap a side-by-side label would be too narrow to read anything but an
// ellipsis, so the pair stacks instead. Above it the labels stay on one row and are
// capped by calcSideBySideCap so they still cannot touch.
const MARKER_OVERLAP_DISTANCE = 0.3;
const markersOverlap = (dotPos, tickPos) => Math.abs(dotPos - tickPos) < MARKER_OVERLAP_DISTANCE;

// Each label is centred on its own marker, so the pair meets when their half-widths
// together span the gap between the markers. Capping each at the whole gap is what
// keeps them apart: half of one plus half of the other is then exactly the gap.
// A fixed cap can't do this — 45% each overlaps for every gap under 0.45.
export const calcSideBySideCap = (dotPos, tickPos, maxCapPct = 45) =>
  round2(Math.min(maxCapPct, Math.abs(dotPos - tickPos) * 100));

// The axis bound is pinned to a track edge and shares a row with the centred values,
// so those values have to stop short of it rather than filling the track.
const EDGE_LABEL_RESERVE = 0.22;
export const calcEdgeAwareCap = (pos, side, capPct) => {
  const room = side === "left" ? pos - EDGE_LABEL_RESERVE : (1 - pos) - EDGE_LABEL_RESERVE;
  return round2(Math.max(0, Math.min(capPct, room * 200)));
};

// A marker's text is centred on it so it always reads as belonging to that marker.
// Clamping to the track edge instead detaches it from the dot and collides with the
// axis edge label, which is itself pinned to that edge. Width is capped at twice the
// distance to the nearer edge so a centred label truncates rather than spilling out.
export const calcMarkerLabelStyle = (pos, maxWidthCapPct = 45) => ({
  position: "absolute",
  left: `${round2(pos * 100)}%`,
  transform: "translateX(-50%)",
  maxWidth: `${round2(Math.min(maxWidthCapPct, Math.min(pos, 1 - pos) * 200))}%`,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap"
});

// An axis bound is derived from the baseline rather than fetched, so it has no display
// wrapper of its own — it borrows the baseline's to inherit the metric's currency and
// decimal places. Formatter abbreviates on magnitude alone (numbers.formatBigNumber
// compares the signed value against positive thresholds), so the sign is applied here.
export const formatAxisBound = (value, sectionType, kpiId, baselineDisplay) => {
  const kpi = sectionType !== "ratio" && KpiRepo.get(kpiId);
  if (!kpi || !baselineDisplay || typeof baselineDisplay !== "object") return abbreviateNumber(value);
  const formatted = Formatter.format(
    {...baselineDisplay, value: Math.abs(value)},
    {maxDisplayLength: 6, valueFormat: kpi.get("valueFormat")}
  );
  return value < 0 ? `-${formatted}` : formatted;
};

// Once the dot reaches the end of the scale its own value marks that point, so the
// edge label is both redundant and in the way. Kept deliberately tight so the edge
// label survives everywhere it can still be read.
const EDGE_LABEL_MIN_DOT_GAP = 0.06;
export const shouldShowEdgeLabel = (edgeLabel, dotPos) => {
  if (!edgeLabel) return false;
  return edgeLabel.side === "left"
    ? dotPos > EDGE_LABEL_MIN_DOT_GAP
    : dotPos < 1 - EDGE_LABEL_MIN_DOT_GAP;
};

// --- Sub-components (internal use only) ---

// dotPos = compareTo (0–1), tickPos = baseline (always 0.5), edgeLabel = {side, value} or null.
const ComparisonBar = ({dotPos, tickPos, compareToValue, baselineValue, compareToLabel, baselineLabel, variant, edgeLabel, formatEdgeValue = abbreviateNumber}) => {
  const {theme} = React.useContext(CustomThemeContext);
  const fillColor = variant === "good"
    ? theme.palette.dashboards.ahead
    : variant === "bad"
      ? theme.palette.dashboards.behind
      : "#AFB9C0";

  if (dotPos === null) return null;

  const dotLeft = `${dotPos * 100}%`;
  const tickLeft = `${tickPos * 100}%`;
  const fillStart = Math.min(dotPos, tickPos) * 100;
  const fillEnd = Math.max(dotPos, tickPos) * 100;
  const fillLeft = `${fillStart}%`;
  const fillWidth = `${fillEnd - fillStart}%`;
  // Round left corners when fill touches the left edge; always round the far end
  const roundLeft = fillStart <= 0 ? 4 : 0;
  const roundRight = dotPos > tickPos ? 4 : 0;
  const fillRadius = `${roundLeft}px ${roundRight}px ${roundRight}px ${roundLeft}px`;

  // When the two markers sit close together, side-by-side text would collide, so each
  // gets its own row. Stacking rather than nudging horizontally is what lets every
  // label and value stay centred on its own marker — a fractional nudge can't account
  // for how wide the text actually renders.
  const isStacked = markersOverlap(dotPos, tickPos);
  const widthCap = isStacked ? 100 : calcSideBySideCap(dotPos, tickPos);
  const showEdgeLabel = shouldShowEdgeLabel(edgeLabel, dotPos);
  // Rows that carry the axis bound give up width for it; rows that don't keep the lot.
  const valueCap = pos => showEdgeLabel ? calcEdgeAwareCap(pos, edgeLabel.side, widthCap) : widthCap;
  const edgeLabelSpan = showEdgeLabel && (
    <span style={{
      position: "absolute", [edgeLabel.side]: 0,
      color: theme.palette.text.secondary, whiteSpace: "nowrap"
    }}>
      {formatEdgeValue(edgeLabel.value)}
    </span>
  );

  return (
    <div>
      {/* Labels above */}
      {isStacked ? (
        <div style={{fontSize: 11, color: theme.palette.text.main, marginBottom: 2}}>
          <div style={{position: "relative", height: 16}}>
            <span style={calcMarkerLabelStyle(tickPos, widthCap)}>{baselineLabel}</span>
          </div>
          <div style={{position: "relative", height: 16}}>
            <span style={calcMarkerLabelStyle(dotPos, widthCap)}>{compareToLabel}</span>
          </div>
        </div>
      ) : (
        <div style={{position: "relative", height: 18, fontSize: 11, color: theme.palette.text.main, marginBottom: 2}}>
          <span style={calcMarkerLabelStyle(tickPos, widthCap)}>{baselineLabel}</span>
          <span style={calcMarkerLabelStyle(dotPos, widthCap)}>{compareToLabel}</span>
        </div>
      )}

      {/* Track — zIndex:2 so the overflowing dot renders above the values row below */}
      <div style={{position: "relative", height: 8, zIndex: 2}}>
        {/* Grey background */}
        <div style={{position: "absolute", inset: 0, background: "#e0e0e0", borderRadius: 4}} />
        {/* Coloured fill between baseline tick and dot */}
        <div style={{
          position: "absolute", top: 0, bottom: 0,
          left: fillLeft, width: fillWidth,
          background: fillColor, borderRadius: fillRadius
        }} />
        {/* Baseline tick */}
        <div style={{
          position: "absolute", left: tickLeft, top: -3, bottom: -3,
          width: 2, background: theme.palette.text.main, opacity: 0.4,
          transform: "translateX(-50%)"
        }} />
        {/* Dot at compareTo position — bullseye: 14×14 outer, 4×4 inner */}
        <div style={{
          position: "absolute", left: dotLeft, top: "50%",
          transform: "translate(-50%, -50%)",
          width: 14, height: 14, borderRadius: "50%",
          background: theme.palette.text.main,
          display: "flex", alignItems: "center", justifyContent: "center"
        }}>
          <div style={{
            width: 8, height: 8, borderRadius: "50%",
            background: theme.palette.background.card,
            display: "flex", alignItems: "center", justifyContent: "center"
          }}>
            <div style={{width: 4, height: 4, borderRadius: "50%", background: theme.palette.text.main}} />
          </div>
        </div>
      </div>

      {/* Values below — stacked on the same rule as the labels so each value stays
          directly under its own marker */}
      {isStacked ? (
        <div style={{fontSize: 11, color: theme.palette.text.main, marginTop: 4}}>
          <div style={{position: "relative", height: 16}}>
            <span style={calcMarkerLabelStyle(tickPos, valueCap(tickPos))}>{baselineValue}</span>
            {edgeLabelSpan}
          </div>
          <div style={{position: "relative", height: 16}}>
            <span style={{...calcMarkerLabelStyle(dotPos, widthCap), fontWeight: 600}}>{compareToValue}</span>
          </div>
        </div>
      ) : (
        <div style={{position: "relative", height: 18, fontSize: 11, color: theme.palette.text.main, marginTop: 4}}>
          <span style={calcMarkerLabelStyle(tickPos, valueCap(tickPos))}>{baselineValue}</span>
          <span style={{...calcMarkerLabelStyle(dotPos, valueCap(dotPos)), fontWeight: 600}}>{compareToValue}</span>
          {edgeLabelSpan}
        </div>
      )}
    </div>
  );
};

const useBadgeColors = variant => {
  const {theme} = React.useContext(CustomThemeContext);
  const d = theme.palette.dashboards;
  const neutralBg = theme.palette.mode === "dark" ? "#555" : "#f0f0f0";
  return {
    bgColor: variant === "good"
      ? d.aheadBackground
      : variant === "bad"
        ? d.behindBackground
        : neutralBg,
    // Arrow / emphasis colour
    textColor: variant === "good"
      ? d.ahead
      : variant === "bad"
        ? d.behind
        : theme.palette.text.main,
    // Muted subtext colour
    subTextColor: variant === "good"
      ? d.aheadText
      : variant === "bad"
        ? d.behindText
        : theme.palette.text.secondary
  };
};


// bhi-collapse points down; rotate 180° for the up arrow.
const ChangeArrow = ({changePct, size, color}) => {
  if (!changePct) return null;
  return (
    <i
      className="bhi-collapse"
      style={{
        fontSize: size, color, display: "inline-block", lineHeight: 1,
        transform: changePct > 0 ? "rotate(180deg)" : "none"
      }}
    />
  );
};

// Large two-line badge for the hero row: "▲ +2,567%" / "vs. <baselineLabel>"
const HeroBadge = ({changePct, variant, baselineLabel}) => {
  const {bgColor, textColor, subTextColor} = useBadgeColors(variant);

  // A baseline of zero makes any increase infinite rather than unknown, so a badge
  // reading "N/A" claims a gap in the data that isn't there. The bar already shows
  // the two values; showing nothing is the honest reading.
  if (changePct === null) return null;

  const sign = changePct > 0 ? "+" : changePct < 0 ? "-" : "";
  const display = sign + formatChangePct(changePct);

  return (
    <div style={{background: bgColor, borderRadius: 5, padding: "10px 16px", textAlign: "center", minWidth: 90}}>
      <div style={{display: "inline-flex", alignItems: "center", gap: 6, lineHeight: 1}}>
        <ChangeArrow changePct={changePct} size={12} color={textColor} />
        <span style={{fontSize: 24, fontWeight: 600, color: textColor}}>{display}</span>
      </div>
      <div style={{fontSize: 13, fontWeight: 500, color: subTextColor, lineHeight: 1.1, marginTop: 2}}>
        vs. {baselineLabel || "Baseline"}
      </div>
    </div>
  );
};

// Compact single-line pill for bar rows: "▲ +50% vs. <baselineLabel>"
const BarBadge = ({changePct, variant, baselineLabel}) => {
  const {bgColor, textColor, subTextColor} = useBadgeColors(variant);

  // See HeroBadge — no computable change means no badge, not an "N/A" chip.
  if (changePct === null) return null;

  const sign = changePct > 0 ? "+" : changePct < 0 ? "-" : "";

  return (
    <div style={{
      background: bgColor, borderRadius: 5, padding: "4px 10px", whiteSpace: "nowrap",
      display: "inline-flex", alignItems: "center", gap: 3,
      // A long baseline name would otherwise push the pill out over the section title
      minWidth: 0, maxWidth: "100%", overflow: "hidden"
    }}>
      <ChangeArrow changePct={changePct} size={10} color={textColor} />
      <span style={{
        fontSize: 11, fontWeight: 500, color: subTextColor, lineHeight: 1,
        minWidth: 0, overflow: "hidden", textOverflow: "ellipsis"
      }}>
        {sign}{formatChangePct(changePct)} vs. {baselineLabel || "Baseline"}
      </span>
    </div>
  );
};

// Returns a formatted display string for a section value.
// For kpi sections: uses Formatter.format with the kpi's valueFormat.
// For ratio sections: compareToDisplay/baselineDisplay are already formatted strings.
const formatSectionValue = (displayValue, sectionType, kpiId) => {
  if (sectionType === "ratio") return displayValue;
  const kpi = KpiRepo.get(kpiId);
  if (!kpi) return "N/A";
  return Formatter.format(displayValue, {maxDisplayLength: 6, valueFormat: kpi.get("valueFormat")});
};

const heroLabelStyle = theme => ({
  fontSize: 10, color: theme.palette.text.secondary,
  letterSpacing: "0.5px", marginTop: 2, overflowWrap: "anywhere"
});

// Hero section: big numbers + bar + badge
const HeroSection = ({section, sectionData, compareToLabel, baselineLabel, isPriorPeriod, compareToTimeframeLabel, priorPeriodLabel}) => {
  const {theme} = React.useContext(CustomThemeContext);
  const positionCT = sectionData.compareToPositionValue ?? sectionData.compareToValue;
  const positionBL = sectionData.baselinePositionValue ?? sectionData.baselineValue;
  const positions = calcBarPositions(positionCT, positionBL);
  const changePct = calcChangePct(positionCT, positionBL);
  const variant = getBadgeVariant(positionCT, positionBL, section.get("higherIsBetter", true));

  const compareToDisplay = formatSectionValue(
    sectionData.compareToDisplay, section.get("sectionType"), section.get("compareToId")
  );
  const baselineDisplay = formatSectionValue(
    sectionData.baselineDisplay, section.get("sectionType"), section.get("baselineId") ?? section.get("compareToId")
  );
  const formatEdgeValue = value => formatAxisBound(
    value, section.get("sectionType"), section.get("baselineId") ?? section.get("compareToId"), sectionData.baselineDisplay
  );

  return (
    <div>
      <div style={{fontSize: 13, fontWeight: 500, color: theme.palette.text.main, marginBottom: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}>
        {section.get("title")}
      </div>
      {/* Big numbers row — aligned from the top so a label that wraps to three lines
          under one figure doesn't push that figure up out of line with its neighbour.
          The labels break mid-word if they have to, which keeps each column's floor at
          the width of its own figure — narrow enough that the two never collide. */}
      <div style={{display: "flex", alignItems: "flex-start", gap: 16, marginBottom: 12}}>
        <div>
          <div style={{
            fontSize: 42, fontWeight: 700, lineHeight: 1, whiteSpace: "nowrap",
            color: getHeroValueColor(variant)
          }}>
            {compareToDisplay}
          </div>
          {isPriorPeriod ? (
            compareToTimeframeLabel && (
              <div style={{...heroLabelStyle(theme), letterSpacing: undefined}}>
                {compareToTimeframeLabel}
              </div>
            )
          ) : (
            <div style={heroLabelStyle(theme)}>
              {compareToLabel.toUpperCase()}
            </div>
          )}
        </div>
        <div>
          <div style={{fontSize: 42, fontWeight: 500, color: theme.palette.text.secondary, opacity: 0.9, lineHeight: 1, whiteSpace: "nowrap"}}>
            {baselineDisplay}
          </div>
          {isPriorPeriod ? (
            <div style={{...heroLabelStyle(theme), letterSpacing: undefined}}>
              {priorPeriodLabel.replace(/^vs\. /, "")}
            </div>
          ) : (
            <div style={heroLabelStyle(theme)}>
              {baselineLabel.toUpperCase()}
            </div>
          )}
        </div>
        <div style={{marginLeft: "auto", minWidth: 0}}>
          <HeroBadge
            changePct={changePct}
            variant={variant}
            baselineLabel={isPriorPeriod ? priorPeriodLabel.replace(/^vs\. /, "") : baselineLabel}
          />
        </div>
      </div>
      <ComparisonBar
        dotPos={positions?.dotPos ?? null}
        tickPos={positions?.tickPos ?? null}
        edgeLabel={positions?.edgeLabel ?? null}
        formatEdgeValue={formatEdgeValue}
        compareToValue={compareToDisplay}
        baselineValue={baselineDisplay}
        compareToLabel={isPriorPeriod && compareToTimeframeLabel ? compareToTimeframeLabel : compareToLabel}
        baselineLabel={isPriorPeriod ? priorPeriodLabel.replace(/^vs\. /, "") : baselineLabel}
        variant={variant}
      />
    </div>
  );
};

// Subsequent sections: badge top-right + bar only
const BarSection = ({section, sectionData, compareToLabel, baselineLabel, isPriorPeriod, compareToTimeframeLabel, priorPeriodLabel}) => {
  const {theme} = React.useContext(CustomThemeContext);
  const positionCT = sectionData.compareToPositionValue ?? sectionData.compareToValue;
  const positionBL = sectionData.baselinePositionValue ?? sectionData.baselineValue;
  const positions = calcBarPositions(positionCT, positionBL);
  const changePct = calcChangePct(positionCT, positionBL);
  const variant = getBadgeVariant(positionCT, positionBL, section.get("higherIsBetter", true));

  const compareToDisplay = formatSectionValue(
    sectionData.compareToDisplay, section.get("sectionType"), section.get("compareToId")
  );
  const baselineDisplay = formatSectionValue(
    sectionData.baselineDisplay, section.get("sectionType"), section.get("baselineId") ?? section.get("compareToId")
  );
  const formatEdgeValue = value => formatAxisBound(
    value, section.get("sectionType"), section.get("baselineId") ?? section.get("compareToId"), sectionData.baselineDisplay
  );

  return (
    <div>
      {/* Title and badge both give way rather than one being pinned to a fixed share:
          either can be the long one, so a fixed split starves whichever it guesses wrong. */}
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginBottom: 10
      }}>
        <div title={section.get("title")} style={{fontSize: 13, fontWeight: 500, color: theme.palette.text.main, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0}}>
          {section.get("title")}
        </div>
        <BarBadge
          changePct={changePct}
          variant={variant}
          baselineLabel={isPriorPeriod ? priorPeriodLabel.replace(/^vs\. /, "") : baselineLabel}
        />
      </div>
      <ComparisonBar
        dotPos={positions?.dotPos ?? null}
        tickPos={positions?.tickPos ?? null}
        edgeLabel={positions?.edgeLabel ?? null}
        formatEdgeValue={formatEdgeValue}
        compareToValue={compareToDisplay}
        baselineValue={baselineDisplay}
        compareToLabel={isPriorPeriod && compareToTimeframeLabel ? compareToTimeframeLabel : compareToLabel}
        baselineLabel={isPriorPeriod ? priorPeriodLabel.replace(/^vs\. /, "") : baselineLabel}
        variant={variant}
      />
    </div>
  );
};

// Neutral grey used for baseline bars and the neutral comparison fill
const NEUTRAL_GREY = "#AFB9C0";

// Width reserved for section titles rendered to the left of the plot area.
const CHART_TITLE_WIDTH = 96;

// A zero-value bar still has to be hoverable, so every bar keeps a stub wide enough to
// aim at. Kept narrow so the stub reads as "nothing" rather than as a small quantity.
const MIN_BAR_HIT_TARGET = 8;

// Below this the bar is too short to hold its own value, so the value sits outside it.
const VALUE_INSIDE_MIN_PCT = 34;

// A single proportional bar (no background track), labelled with its value and
// repeating that value on hover for bars too short to be read at a glance.
const BarRow = ({fillPct, value, color, insideTextColor}) => {
  const isInside = fillPct >= VALUE_INSIDE_MIN_PCT;
  const label = (
    <span style={{fontSize: 10, fontWeight: 600, whiteSpace: "nowrap"}}>{value}</span>
  );

  return (
    <div style={{height: 20, display: "flex", alignItems: "center"}}>
      <Tooltip title={value} placement="top" disableInteractive>
        <div style={{
          height: 16, width: `${fillPct}%`, minWidth: MIN_BAR_HIT_TARGET, flexShrink: 0,
          background: color, borderRadius: "0 4px 4px 0",
          display: "flex", alignItems: "center", justifyContent: "flex-end",
          paddingRight: isInside ? 6 : 0, boxSizing: "border-box",
          color: insideTextColor, overflow: "hidden"
        }}>
          {isInside && label}
        </div>
      </Tooltip>
      {!isInside && (
        <span style={{marginLeft: 6, minWidth: 0, overflow: "hidden"}}>{label}</span>
      )}
    </div>
  );
};

const LegendItem = ({color, label}) => (
  <span style={{display: "inline-flex", alignItems: "center", gap: 6}}>
    <span style={{width: 12, height: 12, borderRadius: 3, background: color}} />
    {label}
  </span>
);

// Grouped horizontal bar chart across all sections: section titles as left row-labels,
// two bars per section (top = compareTo green/red, bottom = baseline grey) sharing one axis
// scaled to the max value across all sections, with a bottom axis and a legend key.
const BarChartView = ({sections, sectionResults, compareToLabel, baselineLabel}) => {
  const {theme} = React.useContext(CustomThemeContext);

  const rows = sections.map((section, i) => {
    const sd = sectionResults.get(i);
    if (!sd) return null;
    const data = sd.toJS();
    const ctValue = data.compareToPositionValue ?? data.compareToValue;
    const blValue = data.baselinePositionValue ?? data.baselineValue;
    return {
      title: section.get("title") || (() => {
        const id = section.get("compareToId");
        if (!id) return `Section ${i + 1}`;
        return section.get("sectionType", "kpi") === "kpi"
          ? (KpiRepo.get(id)?.get("name") || `Section ${i + 1}`)
          : (RatioRepo.get(id)?.get("name") || `Section ${i + 1}`);
      })(),
      ctValue,
      blValue,
      ctDisplay: formatSectionValue(data.compareToDisplay, section.get("sectionType"), section.get("compareToId")),
      blDisplay: formatSectionValue(data.baselineDisplay, section.get("sectionType"), section.get("baselineId") ?? section.get("compareToId")),
      variant: getBadgeVariant(ctValue, blValue, section.get("higherIsBetter", true))
    };
  }).filter(Boolean);

  const chartMax = Math.max(1, ...rows.map(r => Math.max(Math.abs(r.ctValue ?? 0), Math.abs(r.blValue ?? 0))));
  const fillPct = value => (Math.abs(value ?? 0) / chartMax) * 100;
  const barColor = variant => variant === "good"
    ? theme.palette.dashboards.ahead
    : variant === "bad"
      ? theme.palette.dashboards.behind
      : NEUTRAL_GREY;

  const axisLine = `1px solid ${theme.palette.dashboards.divider}`;

  return (
    <div>
      {rows.map((r, i) => (
        <div key={i} style={{display: "flex", alignItems: "center", marginBottom: 16}}>
          <div style={{
            width: CHART_TITLE_WIDTH, flexShrink: 0, fontSize: 11, fontWeight: 500,
            color: theme.palette.text.main, textAlign: "right", paddingRight: 12,
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"
          }}>
            {r.title}
          </div>
          <div style={{flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 4, borderLeft: axisLine, paddingLeft: 8, color: theme.palette.text.main}}>
            <BarRow fillPct={fillPct(r.ctValue)} value={r.ctDisplay} color={barColor(r.variant)} insideTextColor="#fff" />
            {/* The neutral grey is too light to carry white text */}
            <BarRow fillPct={fillPct(r.blValue)} value={r.blDisplay} color={NEUTRAL_GREY} insideTextColor="#2B2B2B" />
          </div>
        </div>
      ))}

      {/* Bottom axis aligned to the plot area */}
      <div style={{display: "flex"}}>
        <div style={{width: CHART_TITLE_WIDTH, flexShrink: 0}} />
        <div style={{
          flex: 1, marginLeft: 8, borderTop: axisLine, paddingTop: 4,
          fontSize: 10, color: theme.palette.text.secondary,
          display: "flex", justifyContent: "space-between"
        }}>
          <span>0</span>
          <span>{abbreviateNumber(Math.round(chartMax))}</span>
        </div>
      </div>

      {/* Legend key — three entries because primary bars are coloured per-row performance */}
      <div style={{display: "flex", justifyContent: "center", gap: 20, marginTop: 14, fontSize: 11, color: theme.palette.text.secondary}}>
        <LegendItem color={theme.palette.dashboards.ahead} label={`${compareToLabel} (ahead)`} />
        <LegendItem color={theme.palette.dashboards.behind} label={`${compareToLabel} (behind)`} />
        <LegendItem color={NEUTRAL_GREY} label={baselineLabel} />
      </div>
    </div>
  );
};

const VelocityComparison = React.memo(({data, config}) => {
  const {theme} = React.useContext(CustomThemeContext);
  const sections = config.get("sections", Immutable.List());
  const displayType = config.get("displayType", "comparisonBar");
  const compareToLabel = config.getIn(["compareTo", "label"], "Primary");
  const baselineLabel = config.getIn(["baseline", "label"], "Baseline");

  const resolvedTimeframe = config.get("timeframe") ? TimeframeRepo.parse(config.get("timeframe").toJS()) : null;
  const priorTf = resolvedTimeframe ? computePriorTimeframe(resolvedTimeframe) : null;
  const compareToTimeframeLabel = (resolvedTimeframe?.get("name") ?? "").replace(/ to /g, "–");
  const priorPeriodLabel = resolvedTimeframe
    ? formatPriorPeriodVsLabel(resolvedTimeframe.get("id"), resolvedTimeframe.get("name"), priorTf?.start, priorTf?.end)
    : "vs. Prior";

  if (sections.isEmpty()) {
    return <Overlays.Message coverOtherContent={false}>Add sections in the editor</Overlays.Message>;
  }

  // data is the Rata wrapper: access the loaded value then its sections list
  const sectionResults = data.getIn(["value", "sections"], Immutable.List());

  if (displayType === "barChart") {
    return (
      <div style={{padding: "16px 16px 12px 20px", height: "100%", overflowY: "auto"}}>
        <BarChartView
          sections={sections}
          sectionResults={sectionResults}
          compareToLabel={compareToLabel}
          baselineLabel={baselineLabel}
        />
      </div>
    );
  }

  return (
    <div style={{padding: "12px 16px 12px 20px", height: "100%", overflowY: "auto"}}>
      {sections.map((section, i) => {
        const sectionData = sectionResults.get(i);
        if (!sectionData) return null;
        const isPriorPeriod = section.get("comparisonMode", "metric") === "prior_period";
        const props = {
          section, sectionData: sectionData.toJS(), compareToLabel, baselineLabel,
          isPriorPeriod, compareToTimeframeLabel, priorPeriodLabel
        };
        return (
          <React.Fragment key={i}>
            {i > 0 && (
              <div style={{height: 1, background: theme.palette.dashboards.divider, margin: "16px 0"}} />
            )}
            {i === 0 ? <HeroSection {...props} /> : <BarSection {...props} />}
          </React.Fragment>
        );
      })}
    </div>
  );
});

// Shown both on the disabled toggle and in the banner a section already saved in this
// mode falls back to, so the two readings cannot drift apart.
const PRIOR_PERIOD_UNAVAILABLE = "Prior period comparison isn't available for this timeframe.";

const SectionEditor = ({section, index, compareToLabel, baselineLabel, priorComputable, onChange, onRemove, canRemove}) => {
  const {theme} = React.useContext(CustomThemeContext);
  const sectionType = section.get("sectionType", "kpi");
  const isHero = index === 0;
  const isPriorPeriod = section.get("comparisonMode", "metric") === "prior_period";

  const allKpis = React.useMemo(
    () => Immutable.fromJS(KpiRepo.getAll().toJSON()).filter(k => k.get("visible")),
    []
  );
  const allRatios = React.useMemo(
    () => RatioRepo.getAll().toList().filter(r => !r.get("isDeleted")),
    []
  );

  return (
    <div style={{
      border: `1px solid ${isHero ? theme.palette.primary.main : theme.palette.divider}`,
      borderRadius: 6, padding: 14, marginBottom: 12,
      background: isHero ? theme.palette.action.hover : "transparent"
    }}>
      {/* Header */}
      <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12}}>
        <span style={{fontSize: 11, fontWeight: 600, color: theme.palette.text.secondary, letterSpacing: "0.4px"}}>
          {isHero ? "SECTION 1 — HERO" : `SECTION ${index + 1}`}
        </span>
        {canRemove && (
          <IconButton size="small" onClick={onRemove}>
            <i className="bhi-delete" style={{fontSize: 12, color: theme.palette.error.main}} />
          </IconButton>
        )}
      </div>

      {/* Per-section comparison mode toggle */}
      <div style={{marginBottom: 12}}>
        <div style={{fontSize: 11, color: theme.palette.text.secondary, marginBottom: 6}}>Comparison mode</div>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={section.get("comparisonMode", "metric")}
          onChange={(_, val) => {
            if (!val) return;
            if (val === "prior_period" && !priorComputable) return;
            let updated = section.set("comparisonMode", val);
            if (val === "prior_period") updated = updated.delete("baselineId");
            onChange(updated);
          }}
        >
          <ToggleButton value="metric">Metric vs metric</ToggleButton>
          {/* aria-disabled rather than disabled: browsers emit no mouse events at all for
              a disabled <button>, so the tooltip saying why the mode is unavailable could
              never be hovered — the one thing it exists to do. The click is prevented and
              the group's onChange ignores the value, so it is inert either way.
              `value` is repeated on the Tooltip because ToggleButtonGroup reads it off its
              own direct child to work out which button is selected. */}
          <Tooltip
            value="prior_period"
            title={priorComputable ? "" : PRIOR_PERIOD_UNAVAILABLE}
            placement="top">
            <ToggleButton
              value="prior_period"
              aria-disabled={!priorComputable}
              onClick={e => { if (!priorComputable) e.preventDefault(); }}
              style={priorComputable ? undefined : {opacity: 0.6, cursor: "default"}}>
              Period vs prior period
            </ToggleButton>
          </Tooltip>
        </ToggleButtonGroup>
        {isPriorPeriod && !priorComputable && (
          <div style={{
            marginTop: 8, padding: "8px 12px", borderRadius: 4,
            background: theme.palette.warning.light,
            border: `1px solid ${theme.palette.warning.main}`,
            fontSize: 12, color: theme.palette.warning.contrastText
          }}>
            {PRIOR_PERIOD_UNAVAILABLE} Switch to a different timeframe to use this mode.
          </div>
        )}
      </div>

      {/* Section title + type on same row */}
      <div style={{display: "flex", gap: 10, alignItems: "flex-end", marginBottom: 12}}>
        <div style={{flex: 3}}>
          <TextField
            label="Section title"
            size="small"
            fullWidth
            value={section.get("title", "")}
            onChange={e => onChange(section.set("title", e.target.value))}
          />
        </div>
        <div style={{flex: 1}}>
          <div style={{fontSize: 11, color: theme.palette.text.secondary, marginBottom: 6}}>Type</div>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={sectionType}
            onChange={(_, val) => {
              if (!val) return;
              onChange(section
                .set("sectionType", val)
                .delete("compareToId")
                .delete("baselineId")
              );
            }}
          >
            <ToggleButton value="kpi">Metric</ToggleButton>
            <ToggleButton value="ratio">Ratio</ToggleButton>
          </ToggleButtonGroup>
        </div>
      </div>

      {/* Pickers row: one picker in prior period mode, two in metric mode */}
      <div style={{display: "flex", gap: 10, alignItems: "flex-end"}}>
        <div style={{flex: isPriorPeriod ? 6 : 3}}>
          <div style={{fontSize: 11, fontWeight: 600, color: theme.palette.text.secondary, marginBottom: 4}}>
            {isPriorPeriod ? (sectionType === "ratio" ? "Ratio" : "Metric") : compareToLabel}
          </div>
          {sectionType === "kpi"
            ? <KpiPicker
                multiSelect={false}
                clearable={false}
                kpis={allKpis}
                menuPlacement="auto"
                selectedKpiId={section.get("compareToId") ?? null}
                placeholder="Choose metric"
                onChange={id => onChange(section.set("compareToId", id))}
              />
            : <RatioPicker
                label={section.get("compareToId") ? RatioRepo.get(section.get("compareToId"))?.get("name") : "Choose ratio"}
                ratios={allRatios}
                onRatioSelect={r => onChange(section.set("compareToId", r.get("id")))}
                closeOnSelect
              />
          }
        </div>
        {!isPriorPeriod && (
          <div style={{flex: 3}}>
            <div style={{fontSize: 11, fontWeight: 600, color: theme.palette.text.secondary, marginBottom: 4}}>
              {baselineLabel}
            </div>
            {sectionType === "kpi"
              ? <KpiPicker
                  multiSelect={false}
                  clearable={false}
                  kpis={allKpis}
                  menuPlacement="auto"
                  selectedKpiId={section.get("baselineId") ?? null}
                  placeholder="Choose metric"
                  onChange={id => onChange(section.set("baselineId", id))}
                />
              : <RatioPicker
                  label={section.get("baselineId") ? RatioRepo.get(section.get("baselineId"))?.get("name") : "Choose ratio"}
                  ratios={allRatios}
                  onRatioSelect={r => onChange(section.set("baselineId", r.get("id")))}
                  closeOnSelect
                />
            }
          </div>
        )}
        <div style={{flex: 2}}>
          <div style={{fontSize: 11, color: theme.palette.text.secondary, marginBottom: 6}}>Higher is</div>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={section.get("higherIsBetter", true) ? "good" : "bad"}
            onChange={(_, val) => {
              if (!val) return;
              onChange(section.set("higherIsBetter", val === "good"));
            }}
          >
            <ToggleButton value="good">Good</ToggleButton>
            <ToggleButton value="bad">Bad</ToggleButton>
          </ToggleButtonGroup>
        </div>
      </div>

    </div>
  );
};

// Mini preview of the midpoint comparison bar
const ComparisonBarPreview = ({color}) => (
  <svg width="76" height="34" viewBox="0 0 76 34">
    <rect x="4" y="15" width="68" height="5" rx="2.5" fill="#e0e0e0" />
    <rect x="38" y="15" width="18" height="5" rx="2.5" fill={color} />
    <rect x="37" y="12" width="2" height="11" rx="1" fill="#8a8a8a" />
    <circle cx="56" cy="17.5" r="5" fill="#2b2b2b" />
    <circle cx="56" cy="17.5" r="1.6" fill="#fff" />
  </svg>
);

// Mini preview of the grouped horizontal bar chart
const BarChartPreview = ({color}) => (
  <svg width="76" height="34" viewBox="0 0 76 34">
    <rect x="4" y="9" width="56" height="6" rx="3" fill={color} />
    <rect x="4" y="19" width="32" height="6" rx="3" fill="#AFB9C0" />
  </svg>
);

const DISPLAY_TYPES = [
  {value: "comparisonBar", label: "Comparison bars", Preview: ComparisonBarPreview},
  {value: "barChart", label: "Bar chart", Preview: BarChartPreview}
];

const DisplayTypeSelector = ({value, onChange}) => {
  const {theme} = React.useContext(CustomThemeContext);
  return (
    <div style={{marginBottom: 16, marginTop: 16}}>
      <div style={{fontSize: 11, fontWeight: 600, color: theme.palette.text.secondary, letterSpacing: "0.5px", marginBottom: 8}}>
        DISPLAY TYPE
      </div>
      <div style={{display: "flex", gap: 12, maxWidth: "40%"}}>
        {DISPLAY_TYPES.map(({value: optValue, label, Preview}) => {
          const selected = value === optValue;
          return (
            <div
              key={optValue}
              role="button"
              onClick={() => onChange(optValue)}
              data-test-id={`velocity-comparison-display-type-${optValue}`}
              style={{
                flex: 1, cursor: "pointer", borderRadius: 6, padding: 12,
                border: `2px solid ${selected ? theme.palette.primary.main : theme.palette.divider}`,
                background: selected ? theme.palette.action.hover : "transparent",
                display: "flex", flexDirection: "column", alignItems: "center", gap: 8
              }}
            >
              <Preview color={theme.palette.dashboards.ahead} />
              <span style={{fontSize: 12, fontWeight: selected ? 600 : 400, color: theme.palette.text.main}}>
                {label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const VelocityComparisonEditor = React.memo(({component, onComponentChange}) => {
  const {theme} = React.useContext(CustomThemeContext);
  const config = component.get("dataConfig");

  const onConfigChange = React.useCallback(newConfig => {
    onComponentChange(component.set("dataConfig", newConfig));
  }, [component, onComponentChange]);

  const compareToLabel = config.getIn(["compareTo", "label"], "Primary");
  const baselineLabel = config.getIn(["baseline", "label"], "Baseline");
  const sections = config.get("sections", Immutable.List());
  const allPriorPeriod = !sections.isEmpty() && sections.every(s => s.get("comparisonMode", "metric") === "prior_period");
  const timeframe = config.get("timeframe") ? TimeframeRepo.parse(config.get("timeframe").toJS()) : null;
  const priorComputable = isPriorPeriodComputable(timeframe);

  const newBlankSection = () => Immutable.fromJS({
    title: "",
    sectionType: "kpi",
    higherIsBetter: true
  });

  return (
    <div style={{padding: "0.5rem"}}>
      <DataConfigToolbar config={config} onConfigChange={onConfigChange} />

      <DisplayTypeSelector
        value={config.get("displayType", "comparisonBar")}
        onChange={val => onConfigChange(config.set("displayType", val))}
      />

      {/* Group labels — hidden when every section is in period vs prior period mode */}
      {!allPriorPeriod && <div style={{marginBottom: 16, marginTop: 16}}>
        <div style={{fontSize: 11, fontWeight: 600, color: theme.palette.text.secondary, letterSpacing: "0.5px", marginBottom: 8}}>
          COMPARISON GROUPS
        </div>
        <div style={{display: "flex", gap: 12, alignItems: "center"}}>
          <div style={{flex: 1}}>
            <TextField
              label="Label A (primary)"
              size="small"
              fullWidth
              value={compareToLabel}
              onChange={e => onConfigChange(config.setIn(["compareTo", "label"], e.target.value))}
              data-test-id="velocity-comparison-compare-to-label"
            />
          </div>
          <span data-test-id="velocity-comparison-vs-separator" style={{paddingTop: 8, color: theme.palette.text.secondary}}>vs</span>
          <div style={{flex: 1}}>
            <TextField
              label="Label B (baseline)"
              size="small"
              fullWidth
              value={baselineLabel}
              onChange={e => onConfigChange(config.setIn(["baseline", "label"], e.target.value))}
              data-test-id="velocity-comparison-baseline-label"
            />
          </div>
        </div>
      </div>}

      {/* Section cards */}
      {sections.map((section, i) => (
        <SectionEditor
          key={i}
          index={i}
          section={section}
          compareToLabel={compareToLabel}
          baselineLabel={baselineLabel}
          priorComputable={priorComputable}
          canRemove={sections.size > 1}
          onChange={updated => onConfigChange(config.set("sections", sections.set(i, updated)))}
          onRemove={() => onConfigChange(config.set("sections", sections.delete(i)))}
        />
      ))}

      {/* Add section */}
      <Button
        variant="outlined"
        fullWidth
        onClick={() => onConfigChange(config.set("sections", sections.push(newBlankSection())))}
        style={{marginBottom: 16}}
        data-test-id="velocity-comparison-add-section"
      >
        + Add section
      </Button>
    </div>
  );
});

// --- Load helpers ---

const buildAjaxParams = config => ({
  dateFromUI: moment().format("YYYY-MM-DD"),
  timeframe: TimeframeRepo.parse(config.get("timeframe").toJS()),
  clientIds: config.getIn(["clientFilter", "allClientIds"]).toArray(),
  matchAnyTagIds: config.getIn(["tagFilter", "matchAnyTagIds"]).toArray(),
  matchAllTagIds: config.getIn(["tagFilter", "matchAllTagIds"]).toArray(),
  excludedTagIds: config.getIn(["tagFilter", "excludedTagIds"]).toArray(),
  entityToIds: (config.get("entityToIds") ?? Immutable.Map()).toObject(),
  ...DashboardUtils.configToQualifierAjaxParams(config)
});

const loadKpiSection = async (section, compareToAjaxParams, baselineAjaxParams) => {
  const [compareTo, baseline] = await Promise.all([
    KpiCalculator.summary(section.get("compareToId"), compareToAjaxParams),
    KpiCalculator.summary(section.get("baselineId"), baselineAjaxParams)
  ]);
  return {
    compareToValue: compareTo?.total?.value ?? 0,
    baselineValue: baseline?.total?.value ?? 0,
    compareToDisplay: compareTo?.total ?? {value: 0},
    baselineDisplay: baseline?.total ?? {value: 0},
    sectionType: "kpi"
  };
};

const loadRatioSection = async (section, compareToAjaxParams, baselineAjaxParams) => {
  const compareToRatio = RatioRepo.get(section.get("compareToId"));
  const baselineRatio = RatioRepo.get(section.get("baselineId"));

  if (!compareToRatio || !baselineRatio) {
    return {compareToValue: 0, baselineValue: 0, compareToDisplay: "N/A", baselineDisplay: "N/A", sectionType: "ratio"};
  }

  const [ctFirst, ctSecond, blFirst, blSecond] = await Promise.all([
    KpiCalculator.summary(compareToRatio.get("firstKpiId"), compareToAjaxParams),
    KpiCalculator.summary(compareToRatio.get("secondKpiId"), compareToAjaxParams),
    KpiCalculator.summary(baselineRatio.get("firstKpiId"), baselineAjaxParams),
    KpiCalculator.summary(baselineRatio.get("secondKpiId"), baselineAjaxParams)
  ]);

  const ctFirstTotal = ctFirst?.total ?? {value: 0};
  const ctSecondTotal = ctSecond?.total ?? {value: 0};
  const blFirstTotal = blFirst?.total ?? {value: 0};
  const blSecondTotal = blSecond?.total ?? {value: 0};

  const displayType = compareToRatio.get("displayType") || "NORMALISED";

  const compareToDisplay = ratioFormatter.format(displayType, ctFirstTotal, ctSecondTotal);
  const baselineDisplay = ratioFormatter.format(displayType, blFirstTotal, blSecondTotal);

  return {
    compareToValue: getRatioNumericValue(ctFirstTotal.value, ctSecondTotal.value),
    baselineValue: getRatioNumericValue(blFirstTotal.value, blSecondTotal.value),
    compareToPositionValue: parseFloat(compareToDisplay) || 0,
    baselinePositionValue: parseFloat(baselineDisplay) || 0,
    compareToDisplay,
    baselineDisplay,
    sectionType: "ratio"
  };
};

export const loadVelocityComparison = async component => {
  const config = component.get("dataConfig");
  const sections = config.get("sections", Immutable.List());

  if (sections.isEmpty()) return Immutable.fromJS({sections: []});

  const ajaxParams = buildAjaxParams(config);
  const anyPriorPeriod = sections.some(s => s.get("comparisonMode", "metric") === "prior_period");
  const priorTimeframe = anyPriorPeriod ? computePriorTimeframe(ajaxParams.timeframe) : null;
  const baselineAjaxParamsForPrior = priorTimeframe
    ? {...ajaxParams, timeframe: TimeframeRepo.parseCustom(priorTimeframe)}
    : null;

  const sectionResults = await Promise.all(
    sections.map(section => {
      const isPriorPeriod = section.get("comparisonMode", "metric") === "prior_period";

      if (isPriorPeriod) {
        if (!baselineAjaxParamsForPrior) {
          return Promise.resolve({
            compareToValue: 0, baselineValue: 0,
            compareToDisplay: "—", baselineDisplay: "—",
            sectionType: section.get("sectionType", "kpi"),
            notComputable: true
          });
        }
        if (!section.get("compareToId")) {
          return Promise.resolve({
            compareToValue: 0, baselineValue: 0,
            compareToDisplay: "—", baselineDisplay: "—",
            sectionType: section.get("sectionType", "kpi")
          });
        }
        const priorSection = section.set("baselineId", section.get("compareToId"));
        return section.get("sectionType") === "ratio"
          ? loadRatioSection(priorSection, ajaxParams, baselineAjaxParamsForPrior)
          : loadKpiSection(priorSection, ajaxParams, baselineAjaxParamsForPrior);
      }

      const hasCompareTo = !!section.get("compareToId");
      const hasBaseline = !!section.get("baselineId");
      if (!hasCompareTo || !hasBaseline) {
        return Promise.resolve({
          compareToValue: 0, baselineValue: 0,
          compareToDisplay: "—", baselineDisplay: "—",
          sectionType: section.get("sectionType", "kpi")
        });
      }
      return section.get("sectionType") === "ratio"
        ? loadRatioSection(section, ajaxParams, ajaxParams)
        : loadKpiSection(section, ajaxParams, ajaxParams);
    })
  );

  return Immutable.fromJS({sections: sectionResults});
};

export default Immutable.fromJS({
  type: "VelocityComparison",
  label: "Impact Comparison",
  titleIsEditable: true,

  previewDimensions: Immutable.Map({width: 200, height: 150}),
  expandedPreviewDimensions: Immutable.Map({width: 400, height: 300}),

  transformConfig: (backendConfig, dashboardConfig) => {
    const commonConfig = createCommonConfig(backendConfig, dashboardConfig);
    const panelMode = backendConfig.get("comparisonMode", "metric");
    const rawSections = backendConfig.get("sections", Immutable.List());
    const sections = rawSections.map(s =>
      s.has("comparisonMode") ? s : s.set("comparisonMode", panelMode)
    );
    return commonConfig.merge({
      baseline: backendConfig.get("baseline", Immutable.Map({label: "Baseline"})),
      compareTo: backendConfig.get("compareTo", Immutable.Map({label: "Compare To"})),
      sections,
      displayType: backendConfig.get("displayType", "comparisonBar"),
    });
  },

  transformData: data => Immutable.Map({value: data}),

  getTitle: component => component?.get("title") || "Impact Comparison",
  getDefaultData: () => Immutable.Map(),
  getReactComponent: () => VelocityComparison,
  getEditorReactComponent: () => VelocityComparisonEditor,
  canFullScreen: false,
  variant: "scrollable",

  layout: {
    min: {width: 8, height: 6},
    max: {width: 24, height: 32},
    init: {width: 12, height: 16}
  },

  applyParentConfig: (parentConfig, dataConfig) =>
    DashboardUtils.applyInheritanceForKeys(parentConfig, dataConfig),

  load: component => loadVelocityComparison(component)
});
